/**
 * ECharts wrapper.
 *
 * Deliberate choices:
 * * **never a dual y-axis.** Power and irradiance are different scales, so they
 *   get two stacked grids with one axis each and a linked crosshair — not two
 *   scales in one plot;
 * * a crosshair tooltip is always on, because a monitoring chart is read by
 *   hovering;
 * * colours come from the validated categorical order, assigned per entity;
 * * a table view accompanies every chart (WCAG 2.2 and §12: colour is never the
 *   only carrier of meaning).
 */

import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  AxisPointerComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useMemo, useRef, useState } from 'react';

import { readChartTheme } from './palette';

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  AxisPointerComponent,
  DataZoomComponent,
  MarkLineComponent,
  CanvasRenderer,
]);

export interface ChartPanel {
  /** Axis label, e.g. "Power (kW)". One measure per panel — never two scales. */
  axisLabel: string;
  series: Array<{
    name: string;
    /** Stable slot in the categorical order, so filtering never repaints. */
    colorIndex: number;
    /** Time series: [epoch ms, value]. Category series: plain values. */
    points: Array<[number, number | null]> | Array<number | null>;
    area?: boolean;
    kind?: 'line' | 'bar';
  }>;
  /** Discrete x labels (months, plants). Switches the axis to category mode. */
  categories?: string[];
  /** Decimal digits in tooltips and the table. */
  digits?: number;
}

interface Props {
  panels: ChartPanel[];
  height?: number;
  ariaLabel: string;
}

export function Chart({ panels, height = 320, ariaLabel }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const [themeTick, setThemeTick] = useState(0);

  // Follow the OS / app theme: dark mode is its own palette, not an inversion.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => setThemeTick((value) => value + 1);
    media.addEventListener('change', listener);
    const observer = new MutationObserver(listener);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      media.removeEventListener('change', listener);
      observer.disconnect();
    };
  }, []);

  const option = useMemo(() => {
    const theme = readChartTheme(containerRef.current);
    // Stacked panels share the x axis; each gets an equal slice of the plot
    // area, with room at the top for the legend and between panels for the
    // axis name of the lower one.
    const panelCount = Math.max(panels.length, 1);
    const legendSpace = 10; // % reserved at the top
    const slice = (100 - legendSpace) / panelCount;
    const bottomSpace = panelCount > 1 ? 16 : 12; // % per panel for labels

    const grids = panels.map((_, index) => ({
      left: 64,
      right: 24,
      top: `${legendSpace + index * slice}%`,
      height: `${Math.max(slice - bottomSpace, 12)}%`,
      containLabel: false,
    }));

    const axisLine = { lineStyle: { color: theme.grid } };
    const splitLine = { lineStyle: { color: theme.grid, type: 'dashed' as const } };

    return {
      backgroundColor: 'transparent',
      animationDuration: 200,
      textStyle: { color: theme.textSecondary, fontFamily: 'inherit' },
      legend: {
        show: panels.flatMap((panel) => panel.series).length > 1,
        top: 4,
        type: 'scroll' as const,
        icon: 'roundRect',
        itemWidth: 12,
        itemHeight: 3,
        // Legend text wears an ink token; the swatch carries the identity.
        textStyle: { color: theme.textSecondary },
      },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: {
          type: panels.some((panel) => panel.categories)
            ? ('shadow' as const)
            : ('cross' as const),
          label: { backgroundColor: theme.textSecondary },
        },
        backgroundColor: theme.surface,
        borderColor: theme.grid,
        textStyle: { color: theme.textPrimary },
      },
      axisPointer: { link: [{ xAxisIndex: 'all' }] },
      grid: grids,
      xAxis: panels.map((panel, index) => ({
        type: panel.categories ? ('category' as const) : ('time' as const),
        data: panel.categories,
        boundaryGap: Boolean(panel.categories),
        gridIndex: index,
        axisLine,
        axisLabel: {
          color: theme.textMuted,
          hideOverlap: true,
          show: index === panels.length - 1,
        },
        axisTick: { show: false },
        splitLine: { show: false },
      })),
      yAxis: panels.map((panel, index) => ({
        type: 'value' as const,
        gridIndex: index,
        name: panel.axisLabel,
        nameLocation: 'end' as const,
        nameGap: 12,
        nameTextStyle: { color: theme.textMuted, align: 'left' as const },
        axisLine: { show: false },
        axisLabel: { color: theme.textMuted },
        splitLine,
        scale: false,
      })),
      dataZoom: [
        { type: 'inside' as const, xAxisIndex: panels.map((_, index) => index) },
      ],
      series: panels.flatMap((panel, panelIndex) =>
        panel.series.map((series) => {
          const color = theme.categorical[series.colorIndex % theme.categorical.length];
          const common = {
            name: series.name,
            xAxisIndex: panelIndex,
            yAxisIndex: panelIndex,
            data: series.points,
            itemStyle: { color },
            emphasis: { focus: 'series' as const },
          };
          if (series.kind === 'bar') {
            return {
              ...common,
              type: 'bar' as const,
              barMaxWidth: 28,
              // Rounded data-end anchored to the baseline; a 2px gap keeps
              // neighbouring bars from merging into one block.
              itemStyle: { color, borderRadius: [4, 4, 0, 0] as [number, number, number, number] },
              barCategoryGap: '30%',
            };
          }
          return {
            ...common,
            type: 'line' as const,
            showSymbol: false,
            symbolSize: 8,
            // Thin marks; gaps stay gaps — a hole in the data is not interpolated.
            lineStyle: { width: 2, color },
            areaStyle: series.area ? { opacity: theme.dark ? 0.18 : 0.12 } : undefined,
            connectNulls: false,
          };
        }),
      ),
    };
  }, [panels, themeTick]);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!chartRef.current) {
      chartRef.current = echarts.init(containerRef.current, undefined, { renderer: 'canvas' });
    }
    chartRef.current.setOption(option, { notMerge: true });
    const resize = () => chartRef.current?.resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [option]);

  useEffect(
    () => () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    },
    [],
  );

  return (
    <div
      ref={containerRef}
      className="chart"
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
